import React, { Component, type MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import GlobalTheme, { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { AnalyticsMetadata } from '@atlassian/jira-common-constants/src/analytics-metadata';
import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src';
import { injectIntlV2 as injectIntl, FormattedMessage } from '@atlassian/jira-intl';

import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import type { CardProps, GlobalThemeTokens, ThemeTokens } from '../../common/types';
import { CardColor } from '../../common/ui/color';
import { Theme } from '../../common/utils/theme.utils';
import { CardContent } from './card-content';
import messages from './messages';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stopPropagation = (event: any) => event.stopPropagation();

// eslint-disable-next-line jira/react/no-class-components
export class Card extends Component<CardProps> {
	static defaultProps = {
		isDisabled: false,
		isDone: false,
		isSelected: false,
		isFlagged: false,
		isLoading: false,
		persistActions: false,
		typeName: null,
		typeUri: null,
		cardKey: null,
		coverUri: null,
		cover: null,
		avatarName: null,
		avatarUri: null,
		icons: null,
		highlightText: [],
		actions: null,
		color: null,
		customFields: null,
		children: null,
		onClick: noop,
		onSizeChange: noop,
		renderAssignee: null,
		isCMPBoard: false,
		isIPBoard: false,
		status: null,
		hasCompactIssueType: false,
		hasScenarioChanges: false,
		id: null,
		renderIssueLinksStats: () => <></>,
	};

	onClick = (
		event: MouseEvent<HTMLElement>,
		externalAction?: ExternalAction,
		analyticsMetadata?: AnalyticsMetadata,
	) => {
		const { isDisabled, onClick } = this.props;
		if (!isDisabled) {
			onClick(event, externalAction, analyticsMetadata);
		}
	};

	renderMenu() {
		const { actions, isLoading, persistActions } = this.props;

		return (
			!isLoading &&
			!isNil(actions) && (
				<ActionsSection
					data-test-id="platform-card.ui.card.actions-section"
					data-testid="platform-card.ui.card.actions-section"
					persistActions={persistActions}
					onClick={stopPropagation}
				>
					{actions}
				</ActionsSection>
			)
		);
	}

	renderCardColor() {
		const { color, hasScenarioChanges, isIPBoard } = this.props;
		/**
		 * In increment planning boards, we render the card color only if the issue:
		 * * is a new issue created in Plans that doesn't yet exist in Jira
		 * * has scenario changes not yet saved to Jira
		 *
		 * The card color is wrapped with a tooltip to inform the user about the fact that
		 * changes only exist in Plans and need to be saved to Jira to be reflected there.
		 */
		if (isIPBoard) {
			if (hasScenarioChanges) {
				return (
					<Tooltip content={<FormattedMessage {...messages.scenarioChangeTooltip} />}>
						<CardColor color={color} />
					</Tooltip>
				);
			}
			return null;
		}
		return <CardColor color={color} />;
	}

	render() {
		const {
			isDisabled,
			isDone,
			isFlagged,
			isSelected,
			isFlexible,
			isLoading,
			summary,
			typeName,
			typeUri,
			cardKey,
			cardRef,
			coverUri,
			cover,
			color,
			customFields,
			avatarName,
			avatarUri,
			icons,
			highlightText,
			children,
			onSizeChange,
			onBlur,
			onFocus,
			onMouseEnter,
			onMouseLeave,
			renderSummary,
			renderAssignee,
			isCMPBoard,
			isIPBoard,
			status,
			hasCompactIssueType,
			renderIssueLinksStats,
			id,
			hasScenarioChanges,
			fixVersions,
		} = this.props;

		return (
			<Theme.Provider>
				<GlobalTheme.Consumer>
					{({ mode }: GlobalThemeTokens) => (
						<Theme.Consumer
							isDisabled={isDisabled}
							isSelected={isSelected}
							isFlagged={isFlagged}
							mode={mode}
						>
							{(themeTokens: ThemeTokens) => (
								// eslint-disable-next-line styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions
								<Container
									{...themeTokens}
									isDisabled={isDisabled}
									isFlexible={isFlexible}
									onClick={this.onClick}
									// eslint-disable-next-line styled-components-a11y/no-noninteractive-tabindex
									tabIndex={0}
									onFocus={onFocus}
									onBlur={onBlur}
									onMouseEnter={onMouseEnter}
									onMouseLeave={onMouseLeave}
									innerRef={cardRef}
									data-test-id="platform-card.ui.card.focus-container"
									data-testid="platform-card.ui.card.focus-container"
								>
									<CardContent
										id={id}
										isDone={isDone}
										isFlagged={isFlagged}
										isLoading={isLoading}
										summary={summary}
										typeName={typeName}
										typeUri={typeUri}
										cardKey={cardKey}
										coverUri={coverUri}
										cover={cover}
										customFields={customFields}
										avatarName={avatarName}
										avatarUri={avatarUri}
										icons={icons}
										highlightText={highlightText}
										onImageLoad={onSizeChange ?? noop}
										shouldMenuRender
										renderSummary={renderSummary}
										onClick={this.onClick}
										isCMPBoard={isCMPBoard}
										isIPBoard={isIPBoard}
										status={status}
										hasCompactIssueType={hasCompactIssueType}
										renderAssignee={renderAssignee}
										renderIssueLinksStats={renderIssueLinksStats}
										color={color}
										hasScenarioChanges={hasScenarioChanges}
										fixVersions={fixVersions}
									>
										{children}
									</CardContent>
									{!isLoading && this.renderMenu()}
								</Container>
							)}
						</Theme.Consumer>
					)}
				</GlobalTheme.Consumer>
			</Theme.Provider>
		);
	}
}

export default injectIntl(Card);

const EDIT_ICON_CONTENT_SELECTOR = 'issue-field-summary-inline-edit.ui.read.edit-icon';
const STATIC_SUMMARY_CONTENT_SELECTOR = 'issue-field-summary-inline-edit.ui.read.static-summary';
const EDITABLE_SUMMARY_CONTENT_SELECTOR =
	'issue-field-summary-inline-edit.ui.read.editable-summary';

const maxCardWidth = 270;
const boxShadow = token(
	'elevation.shadow.raised',
	'0 1px 1px rgba(23, 43, 77, 0.2), 0 0 1px rgba(23, 43, 77, 0.2)',
);

// eslint-disable-next-line jira/compiled/styled-components-usages, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsSection = styled.div<{ persistActions?: boolean }>((props) => ({
	visibility: props.persistActions ? 'initial' : 'hidden',
	position: 'absolute',
	right: token('space.100', '8px'),
	top: token('space.100', '8px'),
	backgroundColor: token('elevation.surface.raised', colors.N20),
	borderRadius: `${borderRadius}px`,
}));

// eslint-disable-next-line jira/compiled/styled-components-usages, @atlaskit/design-system/no-css-tagged-template-expression
const mixinVisibleActionsStyles = css`
	${/* sc-selector */ ActionsSection} {
		visibility: visible;
	}
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line jira/compiled/styled-components-usages, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	backgroundColor: string;
	textColor: string;
	isDisabled?: boolean;
	isFlexible?: boolean;
	hoverBackgroundColor: string;
	hoverTextColor: string;
}>`
	cursor: pointer;
	user-select: none;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	position: relative;
	margin: ${token('space.025', '2px')} 0;
	border-radius: ${borderRadius}px;
	box-shadow: ${boxShadow};
	transition:
		background-color 140ms ease-in-out,
		color 140ms ease-in-out;
	background-color: ${(props) => props.backgroundColor};
	--jsw-card-background-color: ${({ backgroundColor, isDisabled }) =>
		isDisabled ? token('elevation.surface.raised', 'white') : backgroundColor};
	color: ${(props) => props.textColor};
	filter: ${(props) => (props.isDisabled ? 'grayscale(100%)' : 'none')};
	max-width: ${(props) => (props.isFlexible ? '100%' : `${maxCardWidth}px`)};

	&:hover {
		background-color: ${(props) => props.hoverBackgroundColor};
		--jsw-card-background-color: ${(props) => props.hoverBackgroundColor};
		color: ${(props) => props.hoverTextColor};
	}
	&:focus {
		box-shadow: inset 0 0 0 ${gridSize / 4}px ${token('color.text.brand', colors.B100)};
		outline: none;
	}
	&:hover,
	&:focus,
	&:focus-within {
		${mixinVisibleActionsStyles};
		[data-component-selector='${STATIC_SUMMARY_CONTENT_SELECTOR}'] {
			display: none;
		}
		[data-component-selector='${EDITABLE_SUMMARY_CONTENT_SELECTOR}'],
		[data-component-selector='${EDIT_ICON_CONTENT_SELECTOR}'] {
			display: inline;
		}
	}
`;
